// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 4d6636bb-a07a-4016-bfba-3047ff8dfaea

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Chip, Tooltip, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, InputAdornment,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import { exportCsv, printElementToPdf } from './exports';
import { buildNameIndex, nameKey } from './nameIndex';
import { TypeBadge } from './ui';
import store from './store';

// ---------------------------------------------------------------------------
// Primordia — Interface Hub.
//
// At a real health system nearly every integration routes through one or two
// interface engines (Corepoint, Rhapsody, Cloverleaf, Mirth...). During a cloud
// migration that engine is the single highest-risk chokepoint, and nobody has a
// consolidated view of everything flowing through it. This screen is that view:
//
//   * every engine derived from the `via` values captured on fact sheets,
//   * a "Direct (no engine)" bucket for the feeds that BYPASS the chokepoint
//     (the ones that get missed in cutover planning),
//   * a dense sortable/filterable feed table,
//   * a Ports & firewall panel — exactly what the network team needs to rebuild
//     security groups / firewall rules in the cloud.
//
// Read-only: it derives everything from store.listFactSheets(tenantId).
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

const ALL = '__all__';
const DIRECT = '__direct__';
const DIRECT_LABEL = 'Direct (no engine)';

const DIR_COLORS = { inbound: '#2E7D32', outbound: '#F5A623', bidirectional: BLUE };

const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
const clean = (s) => String(s == null ? '' : s).trim();

// "TCP 6661", "443, 8443", "TCP 6661; TCP 6662" → ['TCP 6661', ...]
function splitPorts(port) {
  const raw = clean(port);
  if (!raw) return [];
  return raw
    .split(/[,;]+|\s+and\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
}

function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
}

// Rendered rows per table. A real interface estate is ~20,000 feeds, and
// mounting one TableRow each is what made this screen unusable; the totals,
// the summary tiles and both CSV exports still cover everything.
const PAGE_SIZE = 100;

// Both of these used to be declared INSIDE the component body, so every render
// produced a new component type — React unmounted and remounted every cell in
// both tables on each keystroke in the search box. At module scope the type is
// stable and a cell only re-renders when its own props change.
function NameCell({ name, id, type, onOpen }) {
  if (!name || name === '—') return <span style={{ color: '#aaa' }}>—</span>;
  const inner = (
    <Stack direction="row" spacing={0.75} alignItems="center" component="span">
      {type ? <TypeBadge type={type} size={16} /> : null}
      <span>{name}</span>
    </Stack>
  );
  if (!id || !onOpen) return <Box component="span" sx={{ color: GREY }}>{inner}</Box>;
  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(id); }}
      sx={{ color: BLUE, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
    >
      {inner}
    </Box>
  );
}

function Tile({ label, value, hint }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 132, flex: '1 1 132px', borderColor: '#e2e8f0' }}>
      <Typography sx={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>{value}</Typography>
      {hint ? <Typography sx={{ fontSize: 11, color: GREY }}>{hint}</Typography> : null}
    </Paper>
  );
}

// One pager, used under both tables.
function ShowMore({ total, limit, onMore, noun }) {
  if (total <= limit) return null;
  const remaining = total - limit;
  return (
    <Box sx={{ p: 1.5, textAlign: 'center' }}>
      <Button size="small" variant="outlined" onClick={onMore}>
        Show {Math.min(PAGE_SIZE, remaining).toLocaleString()} more
        &nbsp;({remaining.toLocaleString()} remaining)
      </Button>
      <Typography variant="caption" sx={{ display: 'block', color: GREY, mt: 0.5 }}>
        Export to CSV for all {total.toLocaleString()} {noun}.
      </Typography>
    </Box>
  );
}

const FEED_COLUMNS = [
  { key: 'source', label: 'Source application' },
  { key: 'target', label: 'Target' },
  { key: 'engine', label: 'Engine' },
  { key: 'protocol', label: 'Protocol' },
  { key: 'port', label: 'Port(s)' },
  { key: 'direction', label: 'Direction' },
  { key: 'dataType', label: 'Data type' },
];

export default function InterfaceHub({ tenantId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [engineKey, setEngineKey] = useState(null); // null = not chosen yet
  const [protocol, setProtocol] = useState(ALL);
  const [direction, setDirection] = useState(ALL);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('source');
  const [sortDir, setSortDir] = useState('asc');
  const [copied, setCopied] = useState('');
  const [feedLimit, setFeedLimit] = useState(PAGE_SIZE);
  const [portLimit, setPortLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    let alive = true;
    store.listFactSheets(tenantId).then((list) => { if (alive) setRows(list || []); });
    return () => { alive = false; };
  }, [tenantId]);

  // Reset the selection whenever the workspace changes.
  useEffect(() => { setEngineKey(null); setProtocol(ALL); setDirection(ALL); setQuery(''); }, [tenantId]);

  // ---- Model: engines + every feed ----------------------------------------
  const model = useMemo(() => {
    // Every key here is tenant-scoped. In the master workspace `rows` spans
    // every client, and a bare name merged them: two health systems that both
    // run Epic produced one target, so a feed drawn from client A's application
    // resolved to client B's fact sheet — and the Target column made that a
    // clickable link. The same key collapsed both clients' "Rhapsody" into a
    // single engine card whose feed count, ports and firewall rules were two
    // estates blended together.
    const byName = buildNameIndex(rows);

    // 1. Engines = the distinct non-empty `via` values, per tenant.
    const engineLabels = new Map(); // key -> { label, tenantId }
    rows.forEach((fs) => (fs.integrations || []).forEach((it) => {
      const via = clean(it && it.via);
      const k = nameKey(fs.tenantId, via);
      if (k) engineLabels.set(k, { label: via, tenantId: fs.tenantId });
    }));
    // 2. A fact sheet whose name matches a `via` value IS that engine — prefer
    //    its canonical fact-sheet name for the label.
    rows.forEach((fs) => {
      const k = nameKey(fs && fs.tenantId, fs && fs.name);
      if (k && engineLabels.has(k)) engineLabels.set(k, { label: fs.name, tenantId: fs.tenantId });
    });

    const feeds = [];
    const feedsPerEngine = new Map();
    rows.forEach((fs) => (fs.integrations || []).forEach((it, i) => {
      if (!it) return;
      const via = clean(it.via);
      const viaKey = nameKey(fs.tenantId, via);
      const target = clean(it.to);
      const targetKey = nameKey(fs.tenantId, target);
      const sourceKey = nameKey(fs.tenantId, fs.name);
      const targetFs = targetKey ? byName.get(targetKey) || null : null;

      // Which chokepoint does this feed belong to?
      //   via set                       → that engine
      //   no via, source IS an engine   → that engine (the engine's own feeds)
      //   no via, target IS an engine   → that engine (a direct hop into it)
      //   otherwise                     → Direct (bypasses every engine)
      let key = DIRECT;
      let implied = false;
      if (viaKey && engineLabels.has(viaKey)) key = viaKey;
      else if (engineLabels.has(sourceKey)) { key = sourceKey; implied = true; }
      else if (engineLabels.has(targetKey)) { key = targetKey; implied = true; }

      const dir = norm(it.direction) || 'outbound';
      feedsPerEngine.set(key, (feedsPerEngine.get(key) || 0) + 1);
      feeds.push({
        id: `${fs.id || fs.name}-${i}`,
        engineKey: key,
        engine: key === DIRECT ? DIRECT_LABEL : (engineLabels.get(key) || {}).label,
        implied,
        source: fs.name || '—',
        sourceId: fs.id || null,
        sourceType: fs.type || null,
        target: target || '—',
        targetId: targetFs ? targetFs.id : null,
        targetType: targetFs ? targetFs.type : null,
        protocol: clean(it.protocol),
        port: clean(it.port),
        ports: splitPorts(it.port),
        direction: dir,
        dataType: clean(it.dataType),
      });
    }));

    // Counted in the feed loop above rather than with a `feeds.filter` per
    // engine, which was O(engines × feeds) on every change to `rows`.
    const engines = [...engineLabels.entries()]
      .map(([key, e]) => ({ key, label: e.label, tenantId: e.tenantId, count: feedsPerEngine.get(key) || 0 }))
      .sort((a, b) => (b.count - a.count)
        || a.label.localeCompare(b.label)
        || String(a.tenantId).localeCompare(String(b.tenantId)));
    const directCount = feedsPerEngine.get(DIRECT) || 0;

    return { feeds, engines, directCount };
  }, [rows]);

  // Default the selection to the busiest engine — that is the chokepoint.
  const selected = engineKey !== null
    ? engineKey
    : (model.engines.length ? model.engines[0].key : ALL);

  // ---- Filtering -----------------------------------------------------------
  const scoped = useMemo(
    () => (selected === ALL ? model.feeds : model.feeds.filter((f) => f.engineKey === selected)),
    [model.feeds, selected]
  );

  const protocolOptions = useMemo(() => {
    const set = new Set();
    scoped.forEach((f) => { if (f.protocol) set.add(f.protocol); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [scoped]);

  const directionOptions = useMemo(() => {
    const set = new Set();
    scoped.forEach((f) => { if (f.direction) set.add(f.direction); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = norm(query);
    let list = scoped;
    if (protocol !== ALL) list = list.filter((f) => f.protocol === protocol);
    if (direction !== ALL) list = list.filter((f) => f.direction === direction);
    if (q) {
      list = list.filter((f) => [f.source, f.target, f.engine, f.protocol, f.port, f.direction, f.dataType]
        .some((v) => norm(v).includes(q)));
    }
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = String(a[sortBy] == null ? '' : a[sortBy]);
      const bv = String(b[sortBy] == null ? '' : b[sortBy]);
      return (av.localeCompare(bv, undefined, { numeric: true }) || a.source.localeCompare(b.source)) * factor;
    });
  }, [scoped, protocol, direction, query, sortBy, sortDir]);

  // ---- Summary -------------------------------------------------------------
  const summary = useMemo(() => {
    const sources = new Set();
    const targets = new Set();
    const ports = new Set();
    const byDirection = {};
    const byProtocol = {};
    filtered.forEach((f) => {
      sources.add(norm(f.source));
      if (f.target && f.target !== '—') targets.add(norm(f.target));
      f.ports.forEach((p) => ports.add(p));
      const d = f.direction || 'outbound';
      byDirection[d] = (byDirection[d] || 0) + 1;
      const p = f.protocol || 'Unspecified';
      byProtocol[p] = (byProtocol[p] || 0) + 1;
    });
    return {
      feeds: filtered.length,
      sources: sources.size,
      targets: targets.size,
      ports: ports.size,
      byDirection: Object.entries(byDirection).sort((a, b) => b[1] - a[1]),
      byProtocol: Object.entries(byProtocol).sort((a, b) => b[1] - a[1]),
    };
  }, [filtered]);

  // ---- Ports & firewall ----------------------------------------------------
  const portRows = useMemo(() => {
    const map = new Map();
    filtered.forEach((f) => {
      const list = f.ports.length ? f.ports : ['(not captured)'];
      list.forEach((p) => {
        const entry = map.get(p) || { port: p, feeds: 0, apps: new Set(), protocols: new Set(), directions: new Set() };
        entry.feeds += 1;
        if (f.source && f.source !== '—') entry.apps.add(f.source);
        if (f.target && f.target !== '—') entry.apps.add(f.target);
        if (f.protocol) entry.protocols.add(f.protocol);
        if (f.direction) entry.directions.add(f.direction);
        map.set(p, entry);
      });
    });
    return [...map.values()]
      .map((e) => ({
        port: e.port,
        feeds: e.feeds,
        apps: [...e.apps].sort((a, b) => a.localeCompare(b)),
        protocols: [...e.protocols].sort((a, b) => a.localeCompare(b)),
        directions: [...e.directions].sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => (b.feeds - a.feeds) || a.port.localeCompare(b.port, undefined, { numeric: true }));
  }, [filtered]);

  // In the master workspace two clients can each run an engine of the same
  // name, so the client has to travel with the label — into the panel heading,
  // the PDF title and the CSV filename, which are the artifacts that leave the
  // product.
  const isMaster = tenantId === 'optimum';
  const selectedEngine = model.engines.find((e) => e.key === selected) || null;
  // The Engine column only renders when no single engine is selected, so the
  // empty-state row has to span the columns actually on screen — it was
  // hardcoded to 7 and overhung the table for every specific engine.
  const feedColumns = useMemo(
    () => FEED_COLUMNS.filter((c) => c.key !== 'engine' || selected === ALL),
    [selected]
  );

  // Only the visible slice mounts. Totals, the summary tiles and both CSV
  // exports are computed over `filtered` / `portRows` in full.
  const pagedFeeds = useMemo(() => filtered.slice(0, feedLimit), [filtered, feedLimit]);
  const pagedPorts = useMemo(() => portRows.slice(0, portLimit), [portRows, portLimit]);
  useEffect(() => { setFeedLimit(PAGE_SIZE); }, [filtered]);
  useEffect(() => { setPortLimit(PAGE_SIZE); }, [portRows]);

  const selectedLabel = selected === ALL
    ? 'All engines'
    : selected === DIRECT
      ? DIRECT_LABEL
      : selectedEngine
        ? `${selectedEngine.label}${isMaster && selectedEngine.tenantId ? ` · ${selectedEngine.tenantId}` : ''}`
        : 'Engine';

  const safeName = selectedLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  // ---- Actions -------------------------------------------------------------
  const setSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const exportFeeds = () => exportCsv(`interface-feeds-${safeName}.csv`, filtered, [
    { label: 'Engine', value: 'engine' },
    { label: 'Source Application', value: 'source' },
    { label: 'Target', value: 'target' },
    { label: 'Protocol', value: 'protocol' },
    { label: 'Port(s)', value: 'port' },
    { label: 'Direction', value: 'direction' },
    { label: 'Data Type', value: 'dataType' },
  ]);

  const exportPorts = () => exportCsv(`interface-ports-${safeName}.csv`, portRows, [
    { label: 'Port', value: 'port' },
    { label: 'Feeds', value: 'feeds' },
    { label: 'Protocols', value: (r) => r.protocols.join(' | ') },
    { label: 'Directions', value: (r) => r.directions.join(' | ') },
    { label: 'Applications', value: (r) => r.apps.join(' | ') },
  ]);

  const copyPorts = () => {
    const text = [
      `Ports through ${selectedLabel}`,
      ['Port', 'Feeds', 'Protocols', 'Directions', 'Applications'].join('\t'),
      ...portRows.map((r) => [r.port, r.feeds, r.protocols.join(', '), r.directions.join(', '), r.apps.join(', ')].join('\t')),
    ].join('\n');
    if (copyToClipboard(text)) {
      setCopied('Copied');
      setTimeout(() => setCopied(''), 1600);
    }
  };

  const engineCards = [
    { key: ALL, label: 'All engines', count: model.feeds.length, kind: 'all' },
    ...model.engines.map((e) => ({ ...e, kind: 'engine' })),
    { key: DIRECT, label: DIRECT_LABEL, count: model.directCount, kind: 'direct' },
  ];

  // ---- Empty state ---------------------------------------------------------
  if (!model.feeds.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>Interface Hub</Typography>
        <Paper sx={{ p: 6, textAlign: 'center', color: GREY }}>
          <HubIcon sx={{ fontSize: 44, color: '#c7d3e0' }} />
          <Typography sx={{ fontWeight: 700, color: HEADER, mt: 1, mb: 0.5 }}>No interface feeds captured yet</Typography>
          <Typography variant="body2" sx={{ maxWidth: 620, mx: 'auto' }}>
            Integrations are captured on each fact sheet under <b>“Integrations &amp; Data Flow”</b> — record the target
            system, the interface engine it routes through (<i>Via</i>), protocol, port, direction and data type.
            Once a few applications are populated, this screen consolidates every feed passing through each engine —
            the single highest-risk chokepoint in a cloud migration.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ---- Render --------------------------------------------------------------
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Interface Hub</Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} variant="outlined" size="small" onClick={exportFeeds}>Feeds CSV</Button>
        <Button startIcon={<DownloadIcon />} variant="outlined" size="small" onClick={exportPorts}>Ports CSV</Button>
        <Button
          startIcon={<PictureAsPdfIcon />}
          variant="outlined"
          size="small"
          onClick={() => printElementToPdf('interface-hub-print', `Interface Hub — ${selectedLabel}`)}
        >
          PDF
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 900 }}>
        Everything flowing through each interface engine, consolidated. The <b>{DIRECT_LABEL}</b> bucket is the set of
        feeds that bypass the engines entirely — those are the ones that get missed at cutover.
      </Typography>

      {/* ---- Engine selector ---- */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        {engineCards.map((e) => {
          const active = e.key === selected;
          return (
            <Paper
              key={e.key}
              onClick={() => setEngineKey(e.key)}
              elevation={active ? 3 : 0}
              variant={active ? 'elevation' : 'outlined'}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer', minWidth: 190, maxWidth: 320,
                borderColor: '#e2e8f0',
                bgcolor: active ? NAVY : '#fff',
                color: active ? '#fff' : NAVY,
                borderLeft: `4px solid ${e.kind === 'direct' ? '#F5A623' : e.kind === 'all' ? GREY : BLUE}`,
                '&:hover': { borderColor: BLUE },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{e.label}</Typography>
              <Typography sx={{ fontSize: 12, color: active ? '#cfe0f2' : GREY }}>
                {isMaster && e.kind === 'engine' && e.tenantId ? `${e.tenantId} · ` : ''}
                {e.count} feed{e.count === 1 ? '' : 's'}
                {e.kind === 'direct' && e.count > 0 ? ' · bypasses the engine' : ''}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {/* ---- Filters ---- */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Search source, target, data type…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: GREY }} /></InputAdornment> }}
        />
        <TextField select size="small" label="Protocol" value={protocol} onChange={(e) => setProtocol(e.target.value)} sx={{ minWidth: 190 }}>
          <MenuItem value={ALL}>All protocols</MenuItem>
          {protocolOptions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Direction" value={direction} onChange={(e) => setDirection(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value={ALL}>All directions</MenuItem>
          {directionOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: GREY }}>
          {filtered.length} of {scoped.length} feed{scoped.length === 1 ? '' : 's'} shown
        </Typography>
      </Stack>

      <div id="interface-hub-print">
        <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 15, mb: 1 }}>
          {selectedLabel}
        </Typography>

        {/* ---- Summary tiles ---- */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Tile label="Total feeds" value={summary.feeds} />
          <Tile label="Source apps" value={summary.sources} />
          <Tile label="Targets" value={summary.targets} />
          <Tile label="Distinct ports" value={summary.ports} hint="firewall rules to rebuild" />
          <Paper variant="outlined" sx={{ p: 1.5, flex: '2 1 260px', borderColor: '#e2e8f0' }}>
            <Typography sx={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, mb: 0.75 }}>
              By direction
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {summary.byDirection.length ? summary.byDirection.map(([d, n]) => (
                <Chip
                  key={d}
                  size="small"
                  label={`${d} · ${n}`}
                  sx={{ bgcolor: DIR_COLORS[d] || GREY, color: '#fff', fontWeight: 600 }}
                />
              )) : <Typography variant="caption" sx={{ color: GREY }}>—</Typography>}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: '2 1 300px', borderColor: '#e2e8f0' }}>
            <Typography sx={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, mb: 0.75 }}>
              By protocol
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {summary.byProtocol.length ? summary.byProtocol.map(([p, n]) => (
                <Chip key={p} size="small" label={`${p} · ${n}`} sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 600 }} />
              )) : <Typography variant="caption" sx={{ color: GREY }}>—</Typography>}
            </Stack>
          </Paper>
        </Stack>

        {/* ---- Feed table ---- */}
        <Paper variant="outlined" sx={{ mb: 2, borderColor: '#e2e8f0', overflowX: 'auto' }}>
          <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14, p: 1.5, pb: 1 }}>
            Feeds
          </Typography>
          <Divider />
          <Table size="small" sx={{ '& td': { fontSize: 12.5 } }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEADER, fontSize: 12, whiteSpace: 'nowrap', bgcolor: '#f7f9fb' } }}>
                {feedColumns.map((c) => (
                  <TableCell key={c.key} sortDirection={sortBy === c.key ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === c.key}
                      direction={sortBy === c.key ? sortDir : 'asc'}
                      onClick={() => setSort(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedFeeds.map((f) => (
                <TableRow key={f.id} hover>
                  <TableCell><NameCell name={f.source} id={f.sourceId} type={f.sourceType} onOpen={onOpen} /></TableCell>
                  <TableCell><NameCell name={f.target} id={f.targetId} type={f.targetType} onOpen={onOpen} /></TableCell>
                  {selected === ALL && (
                    <TableCell sx={{ color: f.engineKey === DIRECT ? '#B26A00' : GREY }}>
                      {f.engine}
                      {f.implied ? (
                        <Tooltip title="No “via” captured — matched because the source or target is itself an interface engine.">
                          <Chip size="small" label="derived" sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: '#fff3e0', color: '#8a5a00' }} />
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  )}
                  <TableCell>{f.protocol || <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {f.ports.length
                      ? f.ports.map((p) => <Chip key={p} size="small" label={p} sx={{ bgcolor: '#e8f0fe', mr: 0.5, height: 20, fontSize: 11 }} />)
                      : <span style={{ color: '#aaa' }}>—</span>}
                  </TableCell>
                  <TableCell sx={{ color: DIR_COLORS[f.direction] || GREY, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {f.direction}
                  </TableCell>
                  <TableCell>{f.dataType || <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={feedColumns.length} sx={{ color: GREY, textAlign: 'center', py: 4 }}>
                    No feeds match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ShowMore total={filtered.length} limit={feedLimit} noun="feeds"
            onMore={() => setFeedLimit((l) => l + PAGE_SIZE)} />
        </Paper>

        {/* ---- Ports & firewall ---- */}
        <Paper variant="outlined" sx={{ borderColor: '#e2e8f0', overflowX: 'auto' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, pb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>Ports &amp; firewall</Typography>
            <Typography variant="caption" sx={{ color: GREY }}>
              distinct ports through {selectedLabel} and the applications behind each — the rule set to rebuild in the cloud
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyPorts}>{copied || 'Copy'}</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={exportPorts}>CSV</Button>
          </Stack>
          <Divider />
          <Table size="small" sx={{ '& td': { fontSize: 12.5, verticalAlign: 'top' } }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEADER, fontSize: 12, whiteSpace: 'nowrap', bgcolor: '#f7f9fb' } }}>
                <TableCell>Port</TableCell>
                <TableCell>Feeds</TableCell>
                <TableCell>Protocol(s)</TableCell>
                <TableCell>Direction(s)</TableCell>
                <TableCell>Applications behind this port</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedPorts.map((r) => (
                <TableRow key={r.port} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" label={r.port} sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: NAVY }}>{r.feeds}</TableCell>
                  <TableCell sx={{ color: GREY }}>{r.protocols.join(', ') || '—'}</TableCell>
                  <TableCell sx={{ color: GREY }}>{r.directions.join(', ') || '—'}</TableCell>
                  <TableCell sx={{ color: GREY }}>{r.apps.join(', ') || '—'}</TableCell>
                </TableRow>
              ))}
              {!portRows.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: GREY, textAlign: 'center', py: 4 }}>
                    No ports captured for the current selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ShowMore total={portRows.length} limit={portLimit} noun="ports"
            onMore={() => setPortLimit((l) => l + PAGE_SIZE)} />
        </Paper>
      </div>
    </Box>
  );
}
